// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Global administrators change settings inherited by every
 * workspace for an application on this page. Save each control independently
 * so one rejected setting does not discard unrelated changes. Keep the I-9
 * reset behind typed confirmation because it removes application records
 * across all environments, not just the workspace currently selected.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Switch, Label, Button, SegmentedTabs,
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger, Input,
} from '@/components/ui';
import { apiClient } from '../api/api-client';

interface ApplicationSettings {
  appId: string;
  name: string;
  description?: string | null;
  enablePrefixSetting?: boolean | null;
  slotsEnabled?: boolean | null;
  dashboardTitle?: string | null;
}

interface I9ProcessorSettings {
  snsBatchSize?: number | null;
  syncPageSize?: number | null;
  syncMaxPagesPerRun?: number | null;
  stageConcurrency?: number | null;
}

interface I9ProcessorSettingsInput {
  snsBatchSize: number;
  syncPageSize: number;
  syncMaxPagesPerRun: number;
  stageConcurrency: number;
}

type SettingField = 'enablePrefixSetting' | 'slotsEnabled' | 'dashboardTitle';
type SettingValue = boolean | string | null;
type AdminTab = 'applications' | 'i9-processor';

const inputClass = 'w-48 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-900 transition-[border-color,box-shadow] duration-[120ms] placeholder:text-slate-400 focus:border-[#93c5fd] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] focus:outline-none disabled:bg-slate-50 disabled:text-slate-500';

export default function ApplicationsAdminPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AdminTab>('applications');

  const { data: applications, isLoading, error } = useQuery<ApplicationSettings[]>({
    queryKey: ['admin-applications'],
    queryFn: async () => {
      const res = await apiClient.get<ApplicationSettings[]>('/admin/applications');
      return res.data;
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ appId, field, value }: { appId: string; field: SettingField; value: SettingValue }) => {
      const body: Partial<Record<SettingField, SettingValue>> = { [field]: value };
      await apiClient.request({ url: `/admin/applications/${appId}`, method: 'PATCH', data: body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
      // Workspace configs inherit from the application — invalidate so the UI picks up the change.
      void queryClient.invalidateQueries({ queryKey: ['workspaceConfig'] });
      toast.success('Setting updated');
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Failed to update setting');
    },
  });

  const { data: i9Settings } = useQuery<I9ProcessorSettings>({
    queryKey: ['admin-i9-processor-settings'],
    queryFn: async () => {
      const res = await apiClient.get<I9ProcessorSettings>('/admin/applications/i9-processor-settings');
      return res.data;
    },
  });

  const i9Mutation = useMutation({
    mutationFn: async (settings: I9ProcessorSettingsInput) => {
      await apiClient.put('/admin/applications/i9-processor-settings', settings);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-i9-processor-settings'] });
      toast.success('Setting updated');
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Failed to update setting');
    },
  });

  const i9ResetMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ deleted: number }>('/admin/applications/i9-processor-reset');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Reset complete — ${data.deleted} records deleted`);
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Reset failed');
    },
  });

  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-[1280px] px-6 pt-9 pb-16 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Application Settings</h1>
            <p className="mt-[5px] text-[13px] text-slate-500">
              Application-wide settings inherited by all workspaces of each application
            </p>
          </div>
          <SegmentedTabs
            tabs={[
              { id: 'applications' as const, label: 'Applications' },
              { id: 'i9-processor' as const, label: 'I-9 Processor' },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>

        {isLoading ? (
          <div className="mt-7 flex items-center justify-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            <span className="text-[13px] text-slate-500">Loading applications…</span>
          </div>
        ) : error ? (
          <div className="mt-7 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#991b1b]">
            Failed to load applications.
          </div>
        ) : activeTab === 'applications' ? (
          <>
            <p className="mt-7 mb-3.5 text-[12.5px] text-slate-500">
              Toggles below apply to the application workspace and are inherited by all workspaces of that application.
            </p>
            <div className="space-y-4">
              {(applications ?? []).map((app) => (
                <section
                  key={app.appId}
                  className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-5 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
                >
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-sm font-semibold tracking-[-0.01em]">{app.name}</h2>
                    <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600">
                      {app.appId}
                    </span>
                  </div>
                  {app.description && (
                    <p className="mt-[3px] text-[12.5px] text-slate-500">{app.description}</p>
                  )}

                  <div className="mt-2 divide-y divide-slate-100">
                    <ToggleRow
                      id={`${app.appId}-enable-prefix-setting`}
                      label="Enable Request ID Prefix setting"
                      description="Show the workspace settings page where admins configure the request ID prefix."
                      checked={app.enablePrefixSetting === true}
                      disabled={mutation.isPending}
                      onChange={(value) =>
                        mutation.mutate({ appId: app.appId, field: 'enablePrefixSetting', value })
                      }
                    />
                    <ToggleRow
                      id={`${app.appId}-slots-enabled`}
                      label="Enable agent upload slots"
                      description="Allow agents in workspaces of this application to expose upload slots."
                      checked={app.slotsEnabled === true}
                      disabled={mutation.isPending}
                      onChange={(value) =>
                        mutation.mutate({ appId: app.appId, field: 'slotsEnabled', value })
                      }
                    />
                    <TextRow
                      id={`${app.appId}-dashboard-title`}
                      label="Dashboard sidebar label"
                      description="Custom label for the Dashboard sidebar item in workspaces of this application. Leave blank to use 'Dashboard'."
                      value={app.dashboardTitle ?? ''}
                      disabled={mutation.isPending}
                      onCommit={(value) =>
                        mutation.mutate({
                          appId: app.appId,
                          field: 'dashboardTitle',
                          value: value.trim() === '' ? null : value.trim(),
                        })
                      }
                    />
                  </div>
                </section>
              ))}
              {(applications ?? []).length === 0 && (
                <div className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 text-center shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
                  <div className="text-[13.5px] font-[550] text-slate-700">No applications found</div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="mt-7">
            <I9ProcessorForm
              settings={i9Settings}
              saving={i9Mutation.isPending}
              onSave={(values) => i9Mutation.mutate(values)}
              resetting={i9ResetMutation.isPending}
              onReset={() => i9ResetMutation.mutate()}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface ToggleRowProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function ToggleRow({ id, label, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3.5">
      <div>
        <Label htmlFor={id} className="text-[13px] font-[550] text-slate-800">{label}</Label>
        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

interface TextRowProps {
  id: string;
  label: string;
  description: string;
  value: string;
  disabled: boolean;
  onCommit: (value: string) => void;
}

function TextRow({ id, label, description, value, disabled, onCommit }: TextRowProps) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    if (draft !== value) onCommit(draft);
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3.5">
      <div className="flex-1">
        <Label htmlFor={id} className="text-[13px] font-[550] text-slate-800">{label}</Label>
        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      </div>
      <input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        placeholder="Dashboard"
        className={inputClass}
      />
    </div>
  );
}

interface I9ProcessorFormProps {
  settings?: I9ProcessorSettings;
  saving: boolean;
  onSave: (values: I9ProcessorSettingsInput) => void;
  resetting: boolean;
  onReset: () => void;
}

const RESET_CONFIRM_PHRASE = 'RESET';

function I9ProcessorForm({ settings, saving, onSave, resetting, onReset }: I9ProcessorFormProps) {
  const [snsBatchSize, setSnsBatchSize] = useState('');
  const [syncPageSize, setSyncPageSize] = useState('');
  const [syncMaxPagesPerRun, setSyncMaxPagesPerRun] = useState('');
  const [stageConcurrency, setStageConcurrency] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    setSnsBatchSize(settings?.snsBatchSize?.toString() ?? '');
    setSyncPageSize(settings?.syncPageSize?.toString() ?? '');
    setSyncMaxPagesPerRun(settings?.syncMaxPagesPerRun?.toString() ?? '');
    setStageConcurrency(settings?.stageConcurrency?.toString() ?? '');
  }, [settings]);

  const drafts = [snsBatchSize, syncPageSize, syncMaxPagesPerRun, stageConcurrency];
  const parsed = drafts.map(Number);
  const allValid = drafts.every(
    (draft, i) => draft.trim() !== '' && Number.isInteger(parsed[i]) && parsed[i] > 0,
  );

  return (
    <section className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-5 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <h2 className="text-sm font-semibold tracking-[-0.01em]">I-9 Processor</h2>
      <p className="mt-[3px] text-[12.5px] text-slate-500">
        Global settings for the scheduled I-9 document processor.
      </p>

      <div className="mt-2 divide-y divide-slate-100">
        <NumberField
          id="i9-sns-batch-size"
          label="SNS batch size"
          description="How many documents to push onto SNS per processor run."
          value={snsBatchSize}
          disabled={saving}
          onChange={setSnsBatchSize}
        />
        <NumberField
          id="i9-sync-page-size"
          label="Sync page size"
          description="Rows fetched per Oracle query page when syncing documents from Optix."
          value={syncPageSize}
          disabled={saving}
          onChange={setSyncPageSize}
        />
        <NumberField
          id="i9-sync-max-pages"
          label="Max sync pages per run"
          description="Maximum Optix pages ingested per run (bounds the initial backfill)."
          value={syncMaxPagesPerRun}
          disabled={saving}
          onChange={setSyncMaxPagesPerRun}
        />
        <NumberField
          id="i9-stage-concurrency"
          label="Stage concurrency"
          description="Worker threads that download, stage, and queue documents in parallel per run. Higher pushes more documents per run; keep at or below the Optix Lambda's concurrency."
          value={stageConcurrency}
          disabled={saving}
          onChange={setStageConcurrency}
        />
      </div>
      <div className="pt-3">
        <button
          type="button"
          disabled={saving || !allValid}
          onClick={() =>
            onSave({
              snsBatchSize: parsed[0],
              syncPageSize: parsed[1],
              syncMaxPagesPerRun: parsed[2],
              stageConcurrency: parsed[3],
            })
          }
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[12.5px] font-semibold transition-all duration-[120ms] ${
            saving || !allValid
              ? 'cursor-default bg-slate-100 text-slate-400'
              : 'bg-[#2563eb] text-white hover:brightness-[1.08]'
          }`}
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Save
        </button>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="text-[13px] font-[550] text-slate-800">Reset I-9 records</p>
        <p className="mt-0.5 max-w-2xl text-xs text-slate-500">
          Deletes all I-9 document records, the sync watermark, and the per-status counters
          from DynamoDB. The next processor run will re-discover and re-queue all documents
          from Optix. This runs in every environment, including production.
        </p>
        <AlertDialog
          open={confirmOpen}
          onOpenChange={(open) => {
            setConfirmOpen(open);
            if (!open) setConfirmText('');
          }}
        >
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" className="mt-3" disabled={resetting}>
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset all I-9 records?</AlertDialogTitle>
              <AlertDialogDescription>
                This will delete all I-9 document records, the sync watermark, and the
                per-status counters. The next processor run will re-discover and re-queue all
                documents from Optix from scratch. This cannot be undone, and it applies to
                whichever environment you are in — <strong>including production</strong>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <Label htmlFor="i9-reset-confirm" className="text-sm">
                Type <span className="font-mono font-semibold">{RESET_CONFIRM_PHRASE}</span> to confirm
              </Label>
              <Input
                id="i9-reset-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={RESET_CONFIRM_PHRASE}
                autoComplete="off"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={confirmText !== RESET_CONFIRM_PHRASE}
                onClick={onReset}
              >
                Reset
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </section>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  description: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

function NumberField({ id, label, description, value, disabled, onChange }: NumberFieldProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3.5">
      <div className="flex-1">
        <Label htmlFor={id} className="text-[13px] font-[550] text-slate-800">{label}</Label>
        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      </div>
      <input
        id={id}
        type="number"
        min={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  );
}
